(() => {
  if (!document.body.classList.contains('clwb-foresters-portal')) return;

  const baseCartUrl = window.Theme?.routes?.cart_url || '/cart';
  const portalCartUrl = `${baseCartUrl}?view=foresters-portal`;
  const cartPath = new URL(baseCartUrl, window.location.origin).pathname;
  const cartChangePath = new URL(window.Theme?.routes?.cart_change_url || '/cart/change', window.location.origin).pathname;
  const cartLink = document.querySelector('[data-foresters-cart-link]');
  const cartCount = document.querySelector('[data-foresters-cart-count]');
  const discountNoticeKey = 'clwb_foresters_discount_notice_v1';
  let activeSizeGuideModal;
  let activeSizeGuideTrigger;
  let discountNoticeTimer;
  let discountNoticeDismissedInMemory = false;

  const setCartCount = (count) => {
    const itemCount = Number.isFinite(count) ? count : 0;

    if (cartCount) {
      cartCount.textContent = String(itemCount);
    }

    if (cartLink) {
      const itemText = itemCount === 1 ? 'item' : 'items';
      cartLink.setAttribute('aria-label', `View Foresters cart, ${itemCount} ${itemText}`);
    }
  };

  const countFromCart = (cart) => {
    if (!cart || typeof cart !== 'object') return undefined;

    if (Number.isFinite(cart.totalQuantity)) return cart.totalQuantity;
    if (Number.isFinite(cart.item_count)) return cart.item_count;

    return undefined;
  };

  const getFocusableElements = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);

  const openSizeGuide = (trigger) => {
    const modalId = trigger.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : null;
    const panel = modal?.querySelector('[data-foresters-size-guide-panel]');

    if (!modal || !panel) return;

    activeSizeGuideModal = modal;
    activeSizeGuideTrigger = trigger;
    modal.hidden = false;
    modal.dataset.open = 'true';
    document.documentElement.classList.add('clwb-foresters-size-guide-open');

    const focusTarget = modal.querySelector('[data-foresters-size-guide-close]') || panel;
    focusTarget.focus();
  };

  const closeSizeGuide = () => {
    if (!activeSizeGuideModal) return;

    activeSizeGuideModal.hidden = true;
    activeSizeGuideModal.removeAttribute('data-open');
    document.documentElement.classList.remove('clwb-foresters-size-guide-open');

    const trigger = activeSizeGuideTrigger;
    activeSizeGuideModal = undefined;
    activeSizeGuideTrigger = undefined;
    trigger?.focus();
  };

  const trapSizeGuideFocus = (event) => {
    if (!activeSizeGuideModal || event.key !== 'Tab') return;

    const focusable = getFocusableElements(activeSizeGuideModal);
    if (focusable.length === 0) {
      event.preventDefault();
      activeSizeGuideModal.querySelector('[data-foresters-size-guide-panel]')?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const showBuyNowError = (button, message) => {
    const form = button.closest('form');
    const error = form?.querySelector('[data-foresters-buy-now-error]');

    if (!error) return;

    error.textContent = message;
    error.classList.remove('hidden');
  };

  const clearBuyNowError = (button) => {
    const form = button.closest('form');
    const error = form?.querySelector('[data-foresters-buy-now-error]');

    if (!error) return;

    error.textContent = '';
    error.classList.add('hidden');
  };

  const setBuyNowLoading = (button, isLoading) => {
    const text = button.querySelector('[data-foresters-buy-now-text]');
    const loading = button.querySelector('[data-foresters-buy-now-loading]');

    button.disabled = isLoading;
    button.setAttribute('aria-busy', String(isLoading));

    if (text) text.hidden = isLoading;
    if (loading) loading.hidden = !isLoading;
  };

  const updateBuyNowAvailability = (available) => {
    document.querySelectorAll('[data-foresters-buy-now]').forEach((button) => {
      button.disabled = available === false;
    });
  };

  const getCurrentQuantity = (form) => {
    const quantityInput = form.querySelector('input[name="quantity"]');
    const parsedQuantity = Number.parseInt(quantityInput?.value || '1', 10);

    return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  };

  const buildBuyNowUrl = (form) => {
    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '').trim();
    const quantity = getCurrentQuantity(form);

    if (!variantId) {
      throw new Error('Please choose an available size before continuing.');
    }

    const params = new URLSearchParams();

    for (const [name, value] of formData.entries()) {
      if (!name.startsWith('properties[') || typeof value !== 'string') continue;

      const cleanValue = value.trim();
      if (cleanValue) params.append(name, cleanValue);
    }

    const query = params.toString();
    const permalink = `${baseCartUrl}/${encodeURIComponent(variantId)}:${quantity}`;

    return query ? `${permalink}?${query}` : permalink;
  };

  const handleBuyNow = (button) => {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const form = button.closest('form');
    const productForm = button.closest('product-form-component');

    if (!form) return;

    clearBuyNowError(button);

    if (!form.reportValidity()) return;

    const quantityValidation = productForm?.refs?.quantitySelector?.canAddToCart?.();
    if (quantityValidation && !quantityValidation.canAdd) {
      const max = quantityValidation.maxQuantity;
      showBuyNowError(button, max ? `Only ${max} can be ordered for this size.` : 'Please check the selected quantity.');
      return;
    }

    try {
      setBuyNowLoading(button, true);
      window.location.href = buildBuyNowUrl(form);
    } catch (error) {
      setBuyNowLoading(button, false);
      showBuyNowError(button, error?.message || 'Unable to prepare checkout. Please try again.');
    }
  };

  const discountNoticeDismissed = () => {
    if (discountNoticeDismissedInMemory) return true;

    try {
      return window.sessionStorage?.getItem(discountNoticeKey) === 'dismissed';
    } catch (_) {
      return false;
    }
  };

  const storeDiscountNoticeDismissal = () => {
    try {
      window.sessionStorage?.setItem(discountNoticeKey, 'dismissed');
    } catch (_) {
      discountNoticeDismissedInMemory = true;
    }
  };

  const dismissDiscountNotice = (notice) => {
    if (!notice) return;

    window.clearTimeout(discountNoticeTimer);
    storeDiscountNoticeDismissal();
    notice.dataset.closing = 'true';
    notice.addEventListener('transitionend', () => notice.remove(), { once: true });

    window.setTimeout(() => {
      notice.remove();
    }, 260);
  };

  const scheduleDiscountNoticeDismissal = (notice) => {
    window.clearTimeout(discountNoticeTimer);
    discountNoticeTimer = window.setTimeout(() => dismissDiscountNotice(notice), 12000);
  };

  const showDiscountNotice = () => {
    if (discountNoticeDismissed() || document.querySelector('[data-foresters-discount-notice]')) return;

    const notice = document.createElement('aside');
    notice.className = 'clwb-foresters-discount-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-label', 'Foresters bundle discount');
    notice.setAttribute('data-foresters-discount-notice', '');
    notice.innerHTML = `
      <div class="clwb-foresters-discount-notice__content">
        <p class="clwb-foresters-discount-notice__heading">SAVE 10% ON 3+ ITEMS</p>
        <p>Mix and match any three or more Foresters products. Your discount is applied automatically in the basket.</p>
        <p class="clwb-foresters-discount-notice__line">Use Add to Cart when combining different products.</p>
      </div>
      <button type="button" class="clwb-foresters-discount-notice__close" aria-label="Close discount notice" data-foresters-discount-dismiss>
        <span aria-hidden="true">&times;</span>
      </button>
    `;

    notice.addEventListener('mouseenter', () => window.clearTimeout(discountNoticeTimer));
    notice.addEventListener('mouseleave', () => scheduleDiscountNoticeDismissal(notice));
    notice.addEventListener('focusin', () => window.clearTimeout(discountNoticeTimer));
    notice.addEventListener('focusout', () => scheduleDiscountNoticeDismissal(notice));

    document.body.appendChild(notice);
    window.requestAnimationFrame(() => {
      notice.dataset.visible = 'true';
    });
    scheduleDiscountNoticeDismissal(notice);
  };

  const syncCartCount = async () => {
    try {
      const response = await fetch(`${baseCartUrl}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const cart = await response.json();
      setCartCount(countFromCart(cart));
    } catch (_) {
      // Leave the server-rendered count in place if the cart count refresh fails.
    }
  };

  const isPortalCartPath = () => window.location.pathname === cartPath;

  const ensurePortalCartView = () => {
    if (!isPortalCartPath()) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('view') === 'foresters-portal') return;

    url.searchParams.set('view', 'foresters-portal');
    window.history.replaceState(window.history.state, '', url);
  };

  const preservePortalCartSectionContext = () => {
    if (window.fetch.__clwbForestersPortalFetch) return;

    const nativeFetch = window.fetch.bind(window);

    const portalFetch = (input, init) => {
      try {
        const requestUrl = typeof input === 'string' || input instanceof URL ? input.toString() : input.url;
        const url = new URL(requestUrl, window.location.origin);
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();

        if (
          method === 'POST' &&
          isPortalCartPath() &&
          url.pathname === cartChangePath &&
          typeof init?.body === 'string'
        ) {
          const body = JSON.parse(init.body);

          if (body && typeof body === 'object' && typeof body.sections_url === 'string') {
            return nativeFetch(input, {
              ...init,
              body: JSON.stringify({
                ...body,
                sections_url: portalCartUrl,
              }),
            });
          }
        }
      } catch (_) {
        // Fall back to the native request if this is not a cart section render.
      }

      return nativeFetch(input, init);
    };

    portalFetch.__clwbForestersPortalFetch = true;
    window.fetch = portalFetch;
  };

  const configurePortalOpenCart = () => {
    const openCart = window.Shopify?.actions?.openCart;

    if (!openCart?.configure) return false;

    openCart.configure({
      async handler() {
        window.location.href = portalCartUrl;
      },
    });

    return true;
  };

  const configurePortalOpenCartWhenReady = () => {
    if (configurePortalOpenCart()) return;

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        configurePortalOpenCart();
      },
      { once: true }
    );
  };

  const handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const eventCount = countFromCart(cart) ?? detail?.itemCount;

        if (Number.isFinite(eventCount)) {
          setCartCount(eventCount);
        } else {
          syncCartCount();
        }

        window.requestAnimationFrame(ensurePortalCartView);
      })
      .catch(() => {
        syncCartCount();
      });
  };

  const handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.resource) {
          updateBuyNowAvailability(false);
          return;
        }

        updateBuyNowAvailability(detail.resource.available);
      })
      .catch(() => {
        updateBuyNowAvailability(false);
      });
  };

  ensurePortalCartView();
  preservePortalCartSectionContext();
  configurePortalOpenCartWhenReady();
  setCartCount(Number.parseInt(cartCount?.textContent || '0', 10));

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const openTrigger = target?.closest('[data-foresters-size-guide-open]');
    const closeTrigger = target?.closest('[data-foresters-size-guide-close]');
    const buyNowButton = target?.closest('[data-foresters-buy-now]');
    const discountDismissButton = target?.closest('[data-foresters-discount-dismiss]');

    if (openTrigger) {
      event.preventDefault();
      openSizeGuide(openTrigger);
    } else if (closeTrigger) {
      event.preventDefault();
      closeSizeGuide();
    } else if (buyNowButton) {
      event.preventDefault();
      handleBuyNow(buyNowButton);
    } else if (discountDismissButton) {
      event.preventDefault();
      dismissDiscountNotice(discountDismissButton.closest('[data-foresters-discount-notice]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeSizeGuide();
      return;
    }

    trapSizeGuideFocus(event);
  });

  document.addEventListener('shopify:cart:lines-update', handleCartUpdate);
  document.addEventListener('shopify:product:select', handleProductSelect);
  window.addEventListener('pageshow', syncCartCount);
  window.setTimeout(showDiscountNotice, 1000);
})();
